import { useRouter } from 'next/router';
import type { SyntheticEvent } from 'react';

import type { IClientMicrocreditListItem } from '@src/@types/clienttypes';
import type { AffSub4 } from '@src/@types/microcredits';
import { useCardClick } from '@src/components/ProductList/components/Card/useCardClick';
import { Experiments } from '@src/constants/abTest';
import { PIXELS_TYPES } from '@src/constants/analytics';
import { MONETIZATION_INTERNAL } from '@src/helpers/advertising';
import { sendAdvertisingButtonClickEvent } from '@src/helpers/analyticsEvents';
import { open } from '@src/helpers/navigationHelper';
import { stringifyPath } from '@src/helpers/query';
import { usePixel } from '@src/hooks/usePixel';
import { useTestVariant } from '@src/hooks/useTestVariants';
import { useHasUtmLabel } from '@src/reducers/route';
import { joinUrl } from '@src/utils';
import { isMainPage } from '@src/utils/routing';
import type { LightweightCardOnClickArgs } from 'entities/Microcredits/ui/LightweightCard';
import { BASE_URL } from 'entities/Seo/config';

import { getPositionFromUrl } from './getPositionFromUrl';

const FULL_DEAL_LINK = stringifyPath(joinUrl(BASE_URL, 'mfo/credit'), {
  placement: 'popup_second_page_ref_click',
  backwards_url: joinUrl(BASE_URL, 'zaimy'),
});

export const useCardAnalytics = (
  offer: IClientMicrocreditListItem,
  position: number,
  onClick?: (e: SyntheticEvent, args: LightweightCardOnClickArgs) => void,
) => {
  const router = useRouter();
  const { isBVariant } = useTestVariant(Experiments.POPANDER_OPEN_OFFER);
  const hasUtmLabel = useHasUtmLabel();

  const positionSource = getPositionFromUrl(router);

  const { organization, advertising, name, _id } = offer;

  const { sourceLink, pixelDisplayLink } = useCardClick({ offer, position });

  const source = positionSource || `${sourceLink}|position_${position}`;
  const affSub4: AffSub4 = {
    organizationName: organization.name,
    productName: name,
    organizationId: organization._id,
    productId: _id,
  };

  const { pixelLink: mfoSiteLink, createPixelLink } = usePixel(advertising?.monetization?.pixels?.click, {
    sub: advertising.advSub,
    affSub4,
    type: advertising?.productType,
    source,
    pixelType: PIXELS_TYPES.CLICK,
  });

  const isNotSravniOffers = !offer?._features?.isFullDealOffer && !offer?._features?.isSravniOffer;

  const openMfoSite = (e: SyntheticEvent) => {
    e.stopPropagation();
    e.preventDefault();

    const mfoUrl = createPixelLink();

    if (isBVariant && isMainPage(router.asPath, router.query) && isNotSravniOffers && hasUtmLabel) {
      window.open(FULL_DEAL_LINK, '_blank', 'noopener');
    }

    if (onClick) {
      onClick(e, {
        mfoUrl,
        id: offer._id,
      });

      return;
    }

    sendAdvertisingButtonClickEvent(offer);

    if (!mfoUrl) {
      return;
    }

    if (advertising?.monetization?.kind === MONETIZATION_INTERNAL) {
      router.push(mfoUrl);
      return;
    }

    open({ href: mfoUrl, target: '_blank', features: 'noopener' });
  };

  return { mfoSiteLink, source, affSub4, pixelDisplayLink, openMfoSite };
};
